import { KitButton } from './button';
import { useId, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import type { AppFieldDeclaration } from '../../../contracts/app-v2.mjs';
import type { FormController, FormSnapshot } from '../tier0/form-controller.mjs';
import { FormField, type FieldControlProps } from './form-field';

export interface AppFormProps {
  fields: AppFieldDeclaration[];
  controller: FormController;
  copy: {
    optional: string;
    submit: string;
    summary(count: number): string;
    error(field: AppFieldDeclaration | undefined, code: string): string;
    status(snapshot: FormSnapshot): string | null;
  };
  newOperationId(): string;
  renderControl(field: AppFieldDeclaration, props: FieldControlProps): ReactNode;
  hints?: Record<string, string>;
  secondary?: ReactNode;
  onResult?(result: unknown): void;
}

const lockedStates = new Set(['pending', 'unknown', 'blocked']);

export function AppForm({
  fields,
  controller,
  copy,
  newOperationId,
  renderControl,
  hints = {},
  secondary,
  onResult,
}: AppFormProps) {
  const prefix = useId();
  const formRef = useRef<HTMLFormElement>(null);
  const edited = useRef(new Set<string>());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const snapshot = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot,
  );
  const locked = lockedStates.has(snapshot.status);
  const fieldId = (id: string) => `${prefix}-${id}`;
  const focusField = (id: string) => {
    const form = formRef.current;
    const element = form?.ownerDocument.getElementById(fieldId(id));
    if (element instanceof HTMLElement && form?.contains(element)) element.focus();
  };
  const validateEdited = (id: string) => {
    if (!edited.current.has(id)) return;
    const next = controller.validate().errors[id];
    setErrors((current) => {
      const result = { ...current };
      if (next) result[id] = next;
      else delete result[id];
      return result;
    });
  };
  const onSubmit = async () => {
    if (locked) return;
    const checked = controller.validate();
    setErrors(checked.errors);
    if (!checked.valid) {
      focusField(Object.keys(checked.errors)[0]);
      return;
    }
    const result = await controller.submit(newOperationId());
    onResult?.(result);
  };
  const status = copy.status(snapshot);
  return (
    <form
      ref={formRef}
      className="pc-form"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        void onSubmit();
      }}
    >
      {Object.keys(errors).length >= 2 && (
        <div className="pc-form-summary" role="alert">
          <p>{copy.summary(Object.keys(errors).length)}</p>
          <ul>
            {Object.entries(errors).map(([id, code]) => (
              <li key={id}>
                <a
                  href={`#${fieldId(id)}`}
                  onClick={(event) => {
                    event.preventDefault();
                    focusField(id);
                  }}
                >
                  {copy.error(
                    fields.find((field) => field.id === id),
                    code,
                  )}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
      {status && (
        <div className="pc-notice" data-tone="warning" role="status">
          {status}
        </div>
      )}
      {fields.map((field) => {
        const id = fieldId(field.id);
        const hint = hints[field.id];
        const error = errors[field.id] && copy.error(field, errors[field.id]);
        const describedBy =
          [hint && `${id}-hint`, error && `${id}-error`].filter(Boolean).join(' ') || undefined;
        return (
          <FormField
            key={field.id}
            id={id}
            label={field.label ?? field.name}
            optional={field.required ? undefined : copy.optional}
            hint={hint}
            error={error}
          >
            {renderControl(field, {
              id,
              value: snapshot.values[field.id],
              disabled: locked,
              'aria-invalid': !!error,
              'aria-describedby': describedBy,
              onBlur: () => validateEdited(field.id),
              onValueChange: (value) => {
                controller.setField(field.id, value);
                edited.current.add(field.id);
                if (errors[field.id]) validateEdited(field.id);
              },
            })}
          </FormField>
        );
      })}
      <div className="pc-form-actions">
        {secondary}
        <KitButton variant="primary" type="submit" disabled={locked}>
          {copy.submit}
        </KitButton>
      </div>
    </form>
  );
}
